import React, { useCallback, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled from 'styled-components';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { WorkspaceDetailData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { useLoaderOld } from '../../platform/Hook';
import { Check } from '../../ui/Check';
import { TextButton } from '../../ui/TextButton';
import { useResolve } from '../Container';
import { Crash } from '../Crash';
import { AccountModel } from '../account/AccountModel';
import { EnableIfHasFeature, HideIfHasFeature } from '../common/FeatureElements';
import { labelSorter } from '../dashboard/Models';
import { Flexer, Loading } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { Box, Button, Colors, Prompt } from './../../ui';
import { workspaceCreateViaPopup, workspaceJoin, workspaceLeave } from './WorkspaceCommands';
import { WorkspaceFeatureCallout } from './WorkspaceFeatureCallout';

const XScrollbars = Scrollbars as any;

const Stack = styled.div` 
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
`;

const StackHeading = styled.div`
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    color: #8390a7;
`;

const StackBody = styled.div`
    flex : 1 1 auto;
`;

const StackActions = styled.div`
    flex: 1 1 auto; 
    display: flex;
    align-items: center;
    justify-content: center;
    visibility: hidden;
`;

const StackItem = styled.div`
    display: flex;
    padding: .75rem 1.5rem;
    border-top: solid 1px #e9ebee;

    &:last-child {
        border-bottom: solid 1px #e9ebee;
    }

    &:hover {
        background: #F0F2F5;
        ${StackActions} {
            visibility: visible;
        }
    }
`;

const StackBlock = styled.div<{ push?: boolean }>`
    display: flex;
    flex-direction: column;
    ${p => p.push ? 'margin-left: auto' : ''};
`;

const StackTitle = styled.div`
    font-weight: 450;
`;

const StackDetail = styled.div`
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
`;

const StackDetailInfo = styled.div`
    display: flex;
    align-items: center;
    height: 1.5rem;
    font-size: .875rem;
    color: #8390a7;
`;

const StackDetailBreak = styled.div`
    font-size: .875rem;
    color: #888;
    margin: 0 1rem;
`;

const StackDetailPositive = styled(StackDetailInfo)`
    font-weight: 400;
    color: ${Colors.positive};

    ${Check.Frame} {
        width: 1.2rem;
        height: 1.2rem;
        margin-right: .5rem;
    }
`;

const WorkspaceStackItem: LFC<{ workspace: WorkspaceDetailData }> = ({ workspace }) => {

    const dispatch = useResolve(CommandDispatcher);
    const account = useObserver(useResolve(AccountModel));
    const joined = !!account.workspaces.find(x => x.id === workspace.id);

    const [working, setWorking] = useState(false);
    const onJoin = useCallback(async () => {
        setWorking(true);
        await dispatch(workspaceJoin, [workspace.id]);
        setWorking(false);
    }, []);
    const onLeave = useCallback(async () => {
        setWorking(true);
        await dispatch(workspaceLeave, [workspace.id]);
        setWorking(false);
    }, []);

    return <StackItem>
        <StackBlock>
            <StackTitle>{workspace.label}</StackTitle>
            <StackDetail>
                {joined && <>
                    <StackDetailPositive><Check value={true} /> Joined</StackDetailPositive>
                    <StackDetailBreak>&bull;</StackDetailBreak>
                </>}
                <StackDetailInfo>{workspace.userCount} members</StackDetailInfo>
                <StackDetailBreak>&bull;</StackDetailBreak>
                <StackDetailInfo>{workspace.boardCount} boards</StackDetailInfo>
                <StackDetailBreak>&bull;</StackDetailBreak>
                <StackDetailInfo>{workspace.description || 'No description.'}</StackDetailInfo>
            </StackDetail>
        </StackBlock>
        <StackBlock push>
            <StackActions>
                {!joined && <Button variant="positive" disabled={working} onAction={onJoin}>Join</Button>}
                {joined && <Button variant="positive" disabled={working} onAction={onLeave}>Leave</Button>}
            </StackActions>
        </StackBlock>
    </StackItem>;
};

export const BrowseWorkspacesView: LFC = () => {

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);

    const [ready, workspaces, err] = useLoaderOld(() =>
        api.exec(v2.workspaces.list()).then(x => x.sort(labelSorter)));

    if (!ready) return <Loading />;
    if (err) return <Crash error={err} />;

    return <Stack>
        <StackHeading>
            <StackBlock>
                {workspaces.length} workspaces
            </StackBlock>
            <StackBlock push>
                <TextButton onClick={() => dispatch(workspaceCreateViaPopup)}>
                    <Icon size=".875" src="deqo://icons/deqo/add" /> Create Workspace
                </TextButton>
            </StackBlock>
        </StackHeading>
        <XScrollbars style={{ flex: '1 1 auto' }}>
            {workspaces.map(x => <WorkspaceStackItem key={x.id} workspace={x} />)}
        </XScrollbars>
    </Stack>;
};

export const BrowseWorkspacesPopup: LFC<PopupProps> = ({ dismiss }) =>
    <Prompt onAction={dismiss}>
        <Popup p="0" w="64" h="48">
            <Box padding="1.5 1.5 0">
                <Popup.Close onAction={dismiss} />
                <Popup.Title>Workspace Browser</Popup.Title>
            </Box>
            <Popup.Content>
                <EnableIfHasFeature name="workspaces" flex grow>
                    <BrowseWorkspacesView />
                </EnableIfHasFeature>
                <HideIfHasFeature name="workspaces">
                    <Flexer inset="4">
                        <WorkspaceFeatureCallout />
                    </Flexer>
                </HideIfHasFeature>
            </Popup.Content>
            <Box padding="1.5">
                <Popup.Actions>
                    <Button label={'Close'} onAction={() => dismiss()} />
                </Popup.Actions>
            </Box>
        </Popup>
    </Prompt>;
